export const API = {
  baseUrl: process.env.REACT_APP_API_URL || "http://localhost:8000",
  endpoints: {
    login:          "/auth/login",
    me:             "/auth/me",
    renewSession:   "/auth/renew",
    changePassword: "/users/me/password",
    changeEmail:    "/users/me/email",
    changeUsername: "/users/me/username",
    users:          "/users",
    resendInvite:   (userId: number) => `/users/${userId}/resend-invite`,
    roles:          "/roles",
    dealerGroups:   "/dealer-groups",
    insights: {
      summary:       "/insights/summary",
      users:         "/insights/users",
      health:        "/insights/health",
      digestPreview: "/insights/digest/preview",
    },
    scheduledReports: {
      base:        "/scheduled-reports",
      reportTypes: "/scheduled-reports/report-types",
      byId:        (id: number) => `/scheduled-reports/${id}`,
      runNow:      (id: number) => `/scheduled-reports/${id}/run-now`,
    },
    // A dealer's morning inventory-audit alerts (revenue-assurance plan, Phase 2).
    auditAlerts: {
      base:     "/audit-alerts",
      problems: "/audit-alerts/problems",
      byId:     (id: number) => `/audit-alerts/${id}`,
      sendNow:  (id: number) => `/audit-alerts/${id}/send-now`,
    },
    announcements: {
      base:   "/announcements",
      active: "/announcements/active",
      byId:   (id: number) => `/announcements/${id}`,
    },
    events: {
      pageView:     "/events/page-view",
      error:        "/events/error",
    },
    forgotPassword: "/auth/forgot-password",
    resetPassword:  "/auth/reset-password",
    preferences:    "/users/me/preferences",
    auditRuns:          "/audit/runs",
    auditRooftops:      "/audit/rooftops",
    auditColumns:       "/audit/columns",
    auditProblemTypes:  "/audit/problem-types",
    auditStatusLegend:  "/audit/status-legend",
    auditWorkedMarks:   "/audit/worked-marks",
    auditProblemHistory: "/audit/problem-history",
    auditUpload:        "/audit/upload",
    auditUploadPresign: "/audit/upload/presign",
    auditUploadConfirm: "/audit/upload/confirm",
    auditUploadedFiles: "/audit/uploaded-files",
    auditFileStatus:    "/audit/file-status",
    auditRun:           "/audit/run",
    auditRunJob:        (id: number) => `/audit/run-jobs/${id}`,
    photoListVehicleNotes: (vin: string) =>
      `/photo-list/vehicles/${encodeURIComponent(vin)}/notes`,
    photoListVehicleProgress: (vin: string) =>
      `/photo-list/vehicles/${encodeURIComponent(vin)}/progress`,
    photoListAddedVehicles: "/photo-list/added-vehicles",
    photoListAddedVehicle: (id: number) => `/photo-list/added-vehicles/${id}`,
    photoListReshoots: "/photo-list/reshoots",
    photoListReshoot: (id: number) => `/photo-list/reshoots/${id}`,
    photoListReshootLookup: "/photo-list/reshoots/lookup",
    photoListSettings: {
      base:         "/photo-list/settings",
      seedDefaults: "/photo-list/settings/seed-defaults",
      lots:         "/photo-list/settings/lots",
      lot:          (id: number) => `/photo-list/settings/lots/${id}`,
      stargardAliases: "/photo-list/settings/stargard-aliases",
      stargardAlias:   (id: number) => `/photo-list/settings/stargard-aliases/${id}`,
      reconSteps:   "/photo-list/settings/recon-steps",
      reconStep:    (id: number) => `/photo-list/settings/recon-steps/${id}`,
      reconStepsReorder: "/photo-list/settings/recon-steps/reorder",
      newLotPriorities: "/photo-list/settings/new-lot-priorities",
      newLotPriority:   (id: number) => `/photo-list/settings/new-lot-priorities/${id}`,
      newLotPrioritiesReorder: "/photo-list/settings/new-lot-priorities/reorder",
      usedLotBoosts: "/photo-list/settings/used-lot-boosts",
      usedLotBoost:  (id: number) => `/photo-list/settings/used-lot-boosts/${id}`,
      usedLotBoostsReorder: "/photo-list/settings/used-lot-boosts/reorder",
      highlightRules: "/photo-list/settings/highlight-rules",
      highlightRule:  (id: number) => `/photo-list/settings/highlight-rules/${id}`,
      highlightRulesReorder: "/photo-list/settings/highlight-rules/reorder",
      highlightRulesLoadStarter: "/photo-list/settings/highlight-rules/load-starter",
    },
    performanceReport:     "/performance/report",
    performanceReportXlsx: "/performance/report.xlsx",
    // The "Not on the Website" page (revenue-assurance plan, Phase 3).
    notOnWebsite: {
      base:    "/audit/not-on-website",
      summary: "/audit/not-on-website/summary",
      xlsx:    "/audit/not-on-website.xlsx",
    },
    websiteAuditBase:   "/audit/website",
    websiteAuditRun:    "/audit/website/run",
    vendorCredentials:  "/vendor-credentials",
    websiteScrapeConfig: "/website-scrape-config",
    ingestionRuns:      "/ingestion-runs",
    inventory: {
      records:      "/inventory/records",
      freshness:    "/inventory/freshness",
      upload:        "/inventory/upload",
      digestStatus:  "/inventory/digest-status",
      changes:       "/inventory/changes",
      filterOptions: "/inventory/filter-options",
      vehicleHistory: "/inventory/vehicle-history",
      dealerNotes:   "/inventory/dealer-notes",
      updateFiles:   "/inventory/update/files",
      updateStatus:  "/inventory/update/status",
      updateClear:   "/inventory/update/clear",
      updateRun:     "/inventory/update/run",
      updateHistory: "/inventory/update/history",
    },
    wholesale: {
      session:                "/wholesale/session",
      upload:                 "/wholesale/upload",
      reassign:               (fileId: number) => `/wholesale/files/${fileId}/reassign`,
      deleteFile:             (fileId: number) => `/wholesale/files/${fileId}`,
      draft:                  "/wholesale/draft",
      draftLatest:            "/wholesale/draft/latest",
      rebucket:               (id: number) => `/wholesale/runs/${id}/rebucket`,
      selectCandidate:        (id: number, vehicleId: number) =>
        `/wholesale/runs/${id}/vehicles/${vehicleId}/select-candidate`,
      excludeVehicle:         (id: number, vehicleId: number) =>
        `/wholesale/runs/${id}/vehicles/${vehicleId}/exclude`,
      restoreVehicle:         (id: number, vehicleId: number) =>
        `/wholesale/runs/${id}/vehicles/${vehicleId}/restore`,
      complete:               (id: number) => `/wholesale/runs/${id}/complete`,
      reopen:                 (id: number) => `/wholesale/runs/${id}/reopen`,
      runs:                   "/wholesale/runs",
      runById:                (id: number) => `/wholesale/runs/${id}`,
      clearedFamilies:        "/wholesale/cleared-families",
      clearedFamilyByName:    (family: string) =>
        `/wholesale/cleared-families/${encodeURIComponent(family)}`,
    },
    wholesaleAuction: {
      session:        "/wholesale-auction/session",
      upload:         "/wholesale-auction/upload",
      deleteFile:     (fileId: number) => `/wholesale-auction/files/${fileId}`,
      clearFiles:     "/wholesale-auction/files",
      runs:           "/wholesale-auction/runs",
      runById:        (id: number) => `/wholesale-auction/runs/${id}`,
      completeRun:    (id: number) => `/wholesale-auction/runs/${id}/complete`,
      reopenRun:      (id: number) => `/wholesale-auction/runs/${id}/reopen`,
      editVehicle:    (id: number) => `/wholesale-auction/vehicles/${id}`,
      selectCandidate: (id: number) => `/wholesale-auction/vehicles/${id}/select-candidate`,
      exportXlsx:     (id: number) => `/wholesale-auction/runs/${id}/export-xlsx`,
    },
    imageIndex: {
      rooftops:       "/image-index/rooftops",
      vehicles:       "/image-index/vehicles",
      vehicleDetail:  (vin: string) => `/image-index/vehicles/${encodeURIComponent(vin)}`,
      vehiclePhotos:  (vin: string) => `/image-index/vehicles/${encodeURIComponent(vin)}/photos`,
      processVehicle: (vin: string) => `/image-index/vehicles/${encodeURIComponent(vin)}/process`,
      approveVehiclePhotos: (vin: string) =>
        `/image-index/vehicles/${encodeURIComponent(vin)}/photos/approve`,
      vehicleJob:     (vin: string, jobId: number) =>
        `/image-index/vehicles/${encodeURIComponent(vin)}/jobs/${jobId}`,
      itemDetail:     (stock: string) => `/image-index/items/${encodeURIComponent(stock)}`,
      itemPhotos:     (stock: string) => `/image-index/items/${encodeURIComponent(stock)}/photos`,
      processItem:    (stock: string) => `/image-index/items/${encodeURIComponent(stock)}/process`,
      approveItemPhotos: (stock: string) =>
        `/image-index/items/${encodeURIComponent(stock)}/photos/approve`,
      itemJob:        (stock: string, jobId: number) =>
        `/image-index/items/${encodeURIComponent(stock)}/jobs/${jobId}`,
      overview:       "/image-index/overview",
    },
    photoCapture: {
      repush:       (vin: string) => `/photo-capture/vehicles/${encodeURIComponent(vin)}/repush`,
      sessions:     "/photo-capture/sessions",
      presign:      (sessionId: number) => `/photo-capture/sessions/${sessionId}/photos/presign`,
      confirmPhoto: (sessionId: number, photoId: number) =>
        `/photo-capture/sessions/${sessionId}/photos/${photoId}/confirm`,
      complete:     (sessionId: number) => `/photo-capture/sessions/${sessionId}/complete`,
    },
    dealerPlates: {
      logo:    (dealerGroupId: number) => `/dealer-groups/${dealerGroupId}/logo`,
      plates:  (dealerGroupId: number) => `/dealer-groups/${dealerGroupId}/plates`,
      upload:  (dealerGroupId: number) => `/dealer-groups/${dealerGroupId}/plates/upload`,
      generate:(dealerGroupId: number) => `/dealer-groups/${dealerGroupId}/plates/generate`,
      plate:   (dealerGroupId: number, plateId: number) =>
        `/dealer-groups/${dealerGroupId}/plates/${plateId}`,
      rooftopAssignments: (dealerGroupId: number) =>
        `/dealer-groups/${dealerGroupId}/rooftop-plate-assignments`,
      rooftopPlate: (rooftopId: number) => `/rooftops/${rooftopId}/plate`,
    },
    photoConfiguration: {
      imageTypes:     "/photo-configuration/image-types",
      imageType:      (imageTypeId: number) => `/photo-configuration/image-types/${imageTypeId}`,
      reference:      (imageTypeId: number) =>
        `/photo-configuration/image-types/${imageTypeId}/references`,
      referenceById:  (imageTypeId: number, referenceId: number) =>
        `/photo-configuration/image-types/${imageTypeId}/references/${referenceId}`,
      shotList:       (vehicleType: string) =>
        `/photo-configuration/shot-lists/${encodeURIComponent(vehicleType)}`,
      bucketShots:    (vehicleType: string) =>
        `/photo-configuration/shot-lists/${encodeURIComponent(vehicleType)}/shots`,
      bucketShot:     (vehicleType: string, shotId: number) =>
        `/photo-configuration/shot-lists/${encodeURIComponent(vehicleType)}/shots/${shotId}`,
      seedCanonical:  (vehicleType: string) =>
        `/photo-configuration/shot-lists/${encodeURIComponent(vehicleType)}/seed-canonical`,
      reorder:        (vehicleType: string) =>
        `/photo-configuration/shot-lists/${encodeURIComponent(vehicleType)}/reorder`,
      classify:       "/photo-configuration/classify",
    },
  },
}
